// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { SurveyType } from '../../../api/types';
import { TYPE_PALETTE, autoSlot, hasChosenColor, typeSlot } from '../../../bygning/typeColors';
import { TextLink } from '../../TextLink';
import { FieldGroup } from './FieldGroup';
import { FieldRow } from './FieldRow';
import styles from './Inspector.module.css';

export interface TypeColorPickerProps {
  type: SurveyType;
  canEdit: boolean;
  busy: boolean;
  /** The server keeps the colour (shared with everyone on the case); else this browser only. */
  shared: boolean;
  /** `#rrggbb`, or null for the automatic colour. */
  onPick: (color: string | null) => void;
}

/**
 * Visning › Farve: the type's colour under the Materiale lens, picked from
 * the categorical palette (`bygning/typeColors`), or "Automatisk" — the slot
 * its id gives it. A radio group of swatches; the current one is ringed.
 */
export function TypeColorPicker({ type, canEdit, busy, shared, onPick }: TypeColorPickerProps) {
  const current = typeSlot(type);
  const chosen = hasChosenColor(type);
  const auto = TYPE_PALETTE[autoSlot(type.id)];
  const sub = !chosen
    ? `Automatisk (${auto.label.toLowerCase()}) — bruges i linsen Materiale`
    : shared
      ? 'Bruges i linsen Materiale · gemt på sagen'
      : 'Bruges i linsen Materiale · kun gemt i denne browser (serveren gemmer ikke farver endnu)';
  return (
    <FieldGroup title="Visning">
      <FieldRow
        label="Farve"
        control={
          <div className={styles.swatches} role="radiogroup" aria-label={`Farve for ${type.name}`}>
            {TYPE_PALETTE.map((slot, i) => {
              const on = i === current;
              return (
                <button
                  key={slot.token}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={slot.label}
                  title={slot.label}
                  className={styles.swatchButton}
                  style={{ background: `var(${slot.token})` }}
                  disabled={!canEdit || busy}
                  onClick={() => {
                    if (!on || !chosen) onPick(slot.hex);
                  }}
                />
              );
            })}
          </div>
        }
        after={
          chosen && canEdit ? (
            <TextLink size="sm" nowrap disabled={busy} onClick={() => onPick(null)}>
              Automatisk
            </TextLink>
          ) : undefined
        }
        sub={sub}
      />
    </FieldGroup>
  );
}
